import React, { useState, useEffect, useCallback, useRef } from "react";
import { Box, Typography, FormControl, Select, MenuItem, Card, CardContent, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Chip, CircularProgress, Button, TablePagination, TextField, useTheme, useMediaQuery, Stack } from "@mui/material";
import RiskLegend from "./RiskLegend";
import ShipmentSHAPDetails from "./ShipmentSHAPDetails";
import SummaryCards from "./SummaryCards";
import { apiService } from "../../services/api";

interface OverviewRow {
  sno: string;
  shipmentId: string;
  routeId: string;
  productName: string;
  sourceCountry: string;
  destination: string;
  supplierName: string;
  delayProbability: string;
  predicted: string;
  probOnTime: number;
  shap_reasons?: any;
}

interface SummaryData {
  delayed: number;
  onTime: number;
  highRisk: number;
  total: number;
}

// Helper: produce a user-friendly API error message
const getApiErrorMessage = (err: any): string => {
  if (!err) return 'Failed to load data';
  if (err.code === 'ECONNABORTED' || err.message?.includes('timeout')) {
    return 'Request timeout - The server is taking too long to respond.';
  }
  if (err.code === 'ERR_NETWORK' || err.message?.includes('Network Error')) {
    return 'Network error - Cannot connect to server.';
  }
  if (err.response?.data?.error) {
    return err.response.data.error;
  }
  if (err.message) {
    return err.message;
  }
  return 'Failed to load data';
};

const OverviewPanel: React.FC = () => {
  const [products, setProducts] = useState<string[]>(["All Products"]);
  const [destinations, setDestinations] = useState<string[]>(["All Cities"]);
  const [suppliers, setSuppliers] = useState<string[]>(["All Suppliers"]);
  
  const [pendingProduct, setPendingProduct] = useState("All Products");
  const [pendingDestination, setPendingDestination] = useState("All Cities");
  const [pendingSupplier, setPendingSupplier] = useState("All Suppliers");
  
  const [appliedProduct, setAppliedProduct] = useState("All Products");
  const [appliedDestination, setAppliedDestination] = useState("All Cities");
  const [appliedSupplier, setAppliedSupplier] = useState("All Suppliers");

  const [pendingSearchTerm, setPendingSearchTerm] = useState("");
  const [appliedSearchTerm, setAppliedSearchTerm] = useState("");
  
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [summaryData, setSummaryData] = useState<SummaryData>({ delayed: 0, onTime: 0, highRisk: 0, total: 0 });
  const [shipments, setShipments] = useState<OverviewRow[]>([]);
  
  const [filterOptionsLoaded, setFilterOptionsLoaded] = useState(false);
  const [filterOptionsLoading, setFilterOptionsLoading] = useState(false);
  const [expandedShipmentId, setExpandedShipmentId] = useState<string | null>(null);
  const [sortOrder, setSortOrder] = useState<'asc' | 'desc'>('desc');
  const [selectedSummaryFilter, setSelectedSummaryFilter] = useState<'delayed' | 'onTime' | 'mediumRisk' | null>(null);
  
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [totalRows, setTotalRows] = useState(0);

  // Helper to get chip color based on delay probability
  const getProbChipColor = (delayProbNum: number): string => {
    if (delayProbNum >= 60) return '#ef4444';
    if (delayProbNum >= 40) return '#f59e0b';
    return '#10b981';
  };

  // Helper to get chip background color based on delay probability
  const getProbChipBgColor = (delayProbNum: number): string => {
    if (delayProbNum >= 60) return '#fef2f2';
    if (delayProbNum >= 40) return '#fffbeb';
    return '#ecfdf5';
  };

  // Fetch cascading filter options whenever a pending filter selection changes.
  // Each filter's available options are narrowed based on the other selected filters.
  const fetchFilterOptions = useCallback((overrides?: { product?: string; supplier?: string; destination_city?: string }) => {
    const product = overrides?.product ?? pendingProduct;
    const supplier = overrides?.supplier ?? pendingSupplier;
    const destination_city = overrides?.destination_city ?? pendingDestination;

    const params: Record<string, string> = {};
    if (product && product !== "All Products") params.product = product;
    if (supplier && supplier !== "All Suppliers") params.supplier = supplier;
    if (destination_city && destination_city !== "All Cities") params.destination_city = destination_city;

    setFilterOptionsLoading(true);

    apiService.getFilterOptions(Object.keys(params).length > 0 ? params : undefined)
      .then(res => {
        const options = res.data;
        let hasAnyOptions = false;

        if (options.product && options.product.length > 0) {
          setProducts(["All Products", ...options.product]);
          hasAnyOptions = true;
        }
        if (options.destination_city && options.destination_city.length > 0) {
          setDestinations(["All Cities", ...options.destination_city]);
          hasAnyOptions = true;
        }
        if (options.supplier && options.supplier.length > 0) {
          setSuppliers(["All Suppliers", ...options.supplier]);
          hasAnyOptions = true;
        }

        setFilterOptionsLoaded(hasAnyOptions);
      })
      .catch(err => {
        console.error('[OverviewPanel] Error loading filter options:', err);
      })
      .finally(() => {
        setFilterOptionsLoading(false);
      });
  }, [pendingProduct, pendingSupplier, pendingDestination]);

  // Initial load of filter options (no selections yet)
  useEffect(() => {
    fetchFilterOptions({ product: "All Products", supplier: "All Suppliers", destination_city: "All Cities" });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Handler helpers that update the pending filter AND re-fetch cascading options
  const handleProductChange = (value: string) => {
    setPendingProduct(value);
    // Reset downstream filters when product changes
    setPendingSupplier("All Suppliers");
    setPendingDestination("All Cities");
    fetchFilterOptions({ product: value, supplier: "All Suppliers", destination_city: "All Cities" });
  };

  const handleSupplierChange = (value: string) => {
    setPendingSupplier(value);
    // Reset destination when supplier changes
    setPendingDestination("All Cities");
    fetchFilterOptions({ supplier: value, destination_city: "All Cities" });
  };

  const handleDestinationChange = (value: string) => {
    setPendingDestination(value);
    fetchFilterOptions({ destination_city: value });
  };

  const extractFilterOptions = useCallback((data: any[]) => {
    const productSet = new Set<string>();
    const destinationSet = new Set<string>();
    const supplierSet = new Set<string>();

    data.forEach((row: any) => {
      if (row.product_name) productSet.add(row.product_name);
      if (row.destination_city) destinationSet.add(row.destination_city);
      if (row.supplier_name) supplierSet.add(row.supplier_name);
    });

    if (productSet.size > 0) {
      setProducts(["All Products", ...Array.from(productSet).sort((a, b) => String(a).localeCompare(String(b)))]);
    }
    if (destinationSet.size > 0) {
      setDestinations(["All Cities", ...Array.from(destinationSet).sort((a, b) => String(a).localeCompare(String(b)))]);
    }
    if (supplierSet.size > 0) {
      setSuppliers(["All Suppliers", ...Array.from(supplierSet).sort((a, b) => String(a).localeCompare(String(b)))]);
    }
  }, []);

  const transformApiResponse = useCallback((apiData: any) => {
    const summary = apiData.summary || {};
    const tableData = apiData.table?.data || [];
    
    const summaryResult: SummaryData = {
      delayed: summary.delayed || 0,
      onTime: summary.on_time || 0,
      highRisk: summary.high_risk || 0,
      total: summary.rows || tableData.length || 0,
    };
    
    setTotalRows(summary.rows || tableData.length || 0);
    
    const uniqueTableData = Array.from(
      new Map(tableData.map((row: any) => [row.shipment_id || row.route_id, row])).values()
    );
    
    const transformedShipments: OverviewRow[] = uniqueTableData.map((row: any, index: number) => {
      const probOnTime = row.prob_on_time || 0;
      const delayProb = Math.round((1 - probOnTime) * 100);
      const predicted = row.predicted_class === 1 ? "On Time" : "Delayed";
      
      return {
        sno: String(index + 1),
        shipmentId: row.shipment_id || "SH-" + (row.route_id || index + 1),
        routeId: row.route_id ? String(row.route_id) : "RT" + String(index + 1).padStart(3, '0'),
        productName: row.product_name || "Unknown",
        sourceCountry: row.source_country || "Unknown",
        destination: row.destination_city || "Unknown",
        supplierName: row.supplier_name || "Unknown",
        delayProbability: delayProb + "%",
        predicted: predicted,
        probOnTime: probOnTime,
        shap_reasons: row.shap_top_features || row.shap_reasons
      };
    });
    
    return { summary: summaryResult, shipments: transformedShipments };
  }, []);

  const requestInProgressRef = useRef(false);

  const buildFilters = useCallback(() => {
    const filters: any = {
      limit: rowsPerPage,
      offset: page * rowsPerPage,
      sort: sortOrder === 'desc' ? "prob_on_time" : "-prob_on_time",
    };

    if (selectedSummaryFilter === 'delayed') {
      filters.predicted_class = 0;
    } else if (selectedSummaryFilter === 'onTime') {
      filters.predicted_class = 1;
    } else if (selectedSummaryFilter === 'mediumRisk') {
      filters.risk_level = 'medium';
    }

    if (appliedProduct && appliedProduct !== "All Products") {
      filters.product = appliedProduct;
    }
    if (appliedDestination && appliedDestination !== "All Cities") {
      filters.destination_city = appliedDestination;
    }
    if (appliedSupplier && appliedSupplier !== "All Suppliers") {
      filters.supplier = appliedSupplier;
    }
    if (appliedSearchTerm && appliedSearchTerm.trim() !== '') {
      filters.search = appliedSearchTerm.trim();
    }

    return filters;
  }, [rowsPerPage, page, sortOrder, selectedSummaryFilter, appliedProduct, appliedDestination, appliedSupplier, appliedSearchTerm]);

  const fetchOverviewData = useCallback(async (extractFilters: boolean = false) => {
    if (requestInProgressRef.current) {
      return;
    }

    requestInProgressRef.current = true;
    setLoading(true);
    setError(null);
    
    try {
      const filters = buildFilters();
      const response = await apiService.getPredictiveOverview(filters);
      
      if (extractFilters && response.data.table?.data && !filterOptionsLoaded) {
        extractFilterOptions(response.data.table.data);
      }
      
      const transformedData = transformApiResponse(response.data);
      
      setSummaryData(transformedData.summary);
      setShipments(transformedData.shipments);
      
    } catch (err: any) {
      const errorMessage = getApiErrorMessage(err);
      setError(errorMessage);
      setSummaryData({ delayed: 0, onTime: 0, highRisk: 0, total: 0 });
      setShipments([]);
    } finally {
      setLoading(false);
      requestInProgressRef.current = false;
    }
  }, [buildFilters, extractFilterOptions, transformApiResponse, filterOptionsLoaded]);

  const [initialLoadDone, setInitialLoadDone] = useState(false);

  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));

  useEffect(() => {
    if (!initialLoadDone) {
      fetchOverviewData(true).then(() => {
        setInitialLoadDone(true);
      });
    }
  }, [fetchOverviewData, initialLoadDone]);

  const handleApplyFilters = () => {
    setPage(0);
    setAppliedProduct(pendingProduct);
    setAppliedDestination(pendingDestination);
    setAppliedSupplier(pendingSupplier);
    setAppliedSearchTerm(pendingSearchTerm);
  };

  const handleReset = () => {
    setPage(0);
    setSelectedSummaryFilter(null);
    setPendingProduct("All Products");
    setPendingDestination("All Cities");
    setPendingSupplier("All Suppliers");
    setPendingSearchTerm("");
    setAppliedProduct("All Products");
    setAppliedDestination("All Cities");
    setAppliedSupplier("All Suppliers");
    setAppliedSearchTerm("");
    // Re-fetch all unfiltered options so every dropdown shows the full list
    fetchFilterOptions({ product: "All Products", supplier: "All Suppliers", destination_city: "All Cities" });
  };

  const handleCardClick = (filterType: 'delayed' | 'onTime' | 'mediumRisk' | null) => {
    setSelectedSummaryFilter(filterType);
    setPage(0);
  };

  const handleChangePage = (_event: unknown, newPage: number) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event: React.ChangeEvent<HTMLInputElement>) => {
    setRowsPerPage(Number.parseInt(event.target.value, 10));
    setPage(0);
  };

  const handleSortClick = () => {
    setSortOrder(sortOrder === 'asc' ? 'desc' : 'asc');
    setPage(0);
  };

  const sortedShipments = [...shipments].sort((a, b) => {
    const probA = Number.parseInt(a.delayProbability.replace('%', ''), 10) || 0;
    const probB = Number.parseInt(b.delayProbability.replace('%', ''), 10) || 0;
    return sortOrder === 'asc' ? probA - probB : probB - probA;
  });

  useEffect(() => {
    if (initialLoadDone) {
      fetchOverviewData(false);
    }
  }, [appliedProduct, appliedDestination, appliedSupplier, appliedSearchTerm, fetchOverviewData, initialLoadDone]);

  return (
    <Box sx={{ height: '100%', overflow: 'auto' }}>
      <Box data-tutorial-step="predictive-summary" sx={{ mt: { xs: 4, md: 8 }, mb: { xs: 4, md: 8 } }}>
        <SummaryCards 
          delayed={summaryData.delayed} 
          onTime={summaryData.onTime} 
          highRisk={summaryData.highRisk}
          onCardClick={handleCardClick}
          selectedFilter={selectedSummaryFilter}
        />
      </Box>

      <Card data-tutorial-step="predictive-filters" variant="outlined" sx={{ p: { xs: 2, md: 3 }, mb: 3, borderRadius: 2 }}>
        <Box
          sx={{
            display: "flex",
            flexDirection: isMobile ? "column" : "row",
            alignItems: isMobile ? "stretch" : "flex-end",
            gap: 2,
            flexWrap: isMobile ? "nowrap" : "wrap",
          }}
        >
          <Box sx={{ flex: 1, minWidth: 150 }}>
            <Typography variant="caption" sx={{ display: "block", mb: 1, color: "#64748b" }}>Product</Typography>
            <FormControl fullWidth size="small">
              <Select 
                value={pendingProduct} 
                onChange={(e) => handleProductChange(e.target.value)}
                MenuProps={{
                  PaperProps: {
                    style: { maxHeight: 300, maxWidth: 300 }
                  },
                  disableScrollLock: true
                }}
              >
                {products.map((p) => (
                  <MenuItem key={p} value={p} sx={{ whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', display: 'block' }}>{p}</MenuItem>
                ))}
              </Select>
            </FormControl>
          </Box>
          <Box sx={{ flex: 1, minWidth: 150 }}>
            <Typography variant="caption" sx={{ display: "block", mb: 1, color: "#64748b" }}>Supplier</Typography>
            <FormControl fullWidth size="small">
              <Select 
                value={pendingSupplier} 
                onChange={(e) => handleSupplierChange(e.target.value)}
                disabled={filterOptionsLoading}
                displayEmpty
                renderValue={(val) => filterOptionsLoading ? "Loading..." : (val as string)}
                MenuProps={{
                  PaperProps: {
                    style: { maxHeight: 300, maxWidth: 300 }
                  },
                  disableScrollLock: true
                }}
              >
                {suppliers.map((s) => (
                  <MenuItem key={s} value={s} sx={{ whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', display: 'block' }}>{s}</MenuItem>
                ))}
              </Select>
            </FormControl>
          </Box>
          <Box sx={{ flex: 1, minWidth: 150 }}>
            <Typography variant="caption" sx={{ display: "block", mb: 1, color: "#64748b" }}>Destination</Typography>
            <FormControl fullWidth size="small">
              <Select 
                value={pendingDestination} 
                onChange={(e) => handleDestinationChange(e.target.value)}
                disabled={filterOptionsLoading}
                displayEmpty
                renderValue={(val) => filterOptionsLoading ? "Loading..." : (val as string)}
                MenuProps={{
                  PaperProps: {
                    style: { maxHeight: 300, maxWidth: 300 }
                  },
                  disableScrollLock: true
                }}
              >
                {destinations.map((d) => (
                  <MenuItem key={d} value={d} sx={{ whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', display: 'block' }}>{d}</MenuItem>
                ))}
              </Select>
            </FormControl>
          </Box>
          <Box sx={{ flex: 1, minWidth: 150 }}>
            <Typography variant="caption" sx={{ display: "block", mb: 1, color: "#64748b" }}>Search</Typography>
            <TextField
              fullWidth
              size="small"
              value={pendingSearchTerm}
              onChange={(e) => setPendingSearchTerm(e.target.value)}
              placeholder="Search by Shipment ID, Product, Supplier, etc."
            />
          </Box>
          <Box sx={{ display: 'flex', gap: 1, mt: isMobile ? 1 : 0 }}>
            <Button 
              variant="contained" 
              onClick={handleApplyFilters}
              sx={{ 
                bgcolor: '#EE2D3D', 
                '&:hover': { bgcolor: '#D42534' },
                textTransform: 'none',
                fontWeight: 600,
                px: 3
              }}
            >
              Apply Filters
            </Button>
            <Button 
              variant="outlined" 
              onClick={handleReset}
              sx={{ 
                borderColor: '#e5e7eb',
                color: '#374151',
                '&:hover': { borderColor: '#d1d5db', bgcolor: '#f9fafb' },
                textTransform: 'none',
                fontWeight: 600,
                px: 3
              }}
            >
              Reset
            </Button>
          </Box>
        </Box>
      </Card>
      
      {loading && (
        <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
          <CircularProgress />
        </Box>
      )}

      {error && (
        <Box sx={{ p: 2, mb: 2, backgroundColor: '#fef2f2', borderRadius: 1, border: '1px solid #fecaca' }}>
          <Typography variant="body2" sx={{ color: '#dc2626' }}>
            Error: {error}
          </Typography>
        </Box>
      )}

      {!loading && !error && (
        <>
          <Box data-tutorial-step="predictive-table" sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
            <Typography variant="h6" sx={{ fontWeight: 600 }}>
              Shipments
            </Typography>
            {!isMobile && <RiskLegend />}
          </Box>
          
          {isMobile ? (
            <Stack spacing={1.5} sx={{ mb: 2 }}>
              {shipments.length === 0 ? (
                <Typography variant="body2" color="text.secondary" sx={{ textAlign: 'center', py: 2 }}>
                  No shipments found
                </Typography>
              ) : (
                sortedShipments.map((r) => {
                  const delayProbNum = Number.parseInt(r.delayProbability.replace('%', ''), 10);
                  const probChipColor = getProbChipColor(delayProbNum);
                  const probChipBgColor = getProbChipBgColor(delayProbNum);
                  const isDelayed = r.predicted === "Delayed";

                  return (
                    <Card
                      key={r.shipmentId}
                      variant="outlined"
                      sx={{
                        borderRadius: 2,
                        borderLeft: `4px solid ${probChipColor}`,
                      }}
                      onClick={() =>
                        setExpandedShipmentId(expandedShipmentId === r.shipmentId ? null : r.shipmentId)
                      }
                    >
                      <CardContent sx={{ pb: 1.5 }}>
                        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
                          <Typography sx={{ fontWeight: 600, fontSize: '0.9rem' }}>{r.productName}</Typography>
                          <Chip
                            label={r.delayProbability}
                            size="small"
                            sx={{
                              backgroundColor: probChipBgColor,
                              color: probChipColor,
                              fontWeight: 600,
                              fontSize: '0.7rem',
                            }}
                          />
                        </Box>
                        <Typography sx={{ fontSize: '0.8rem', color: '#64748b' }}>
                          {r.sourceCountry} → {r.destination}
                        </Typography>
                        <Box sx={{ mt: 1, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                          <Chip
                            label={r.predicted}
                            size="small"
                            sx={{
                              backgroundColor: isDelayed ? '#fef2f2' : '#ecfdf5',
                              color: isDelayed ? '#ef4444' : '#10b981',
                              fontWeight: 600,
                              fontSize: '0.7rem',
                            }}
                          />
                          <Typography sx={{ fontSize: '0.75rem', color: '#9ca3af' }}>Supplier: {r.supplierName}</Typography>
                        </Box>

                        {expandedShipmentId === r.shipmentId && (
                          <Box sx={{ mt: 1.5, pt: 1.5, borderTop: '1px solid #e5e7eb' }}>
                            <ShipmentSHAPDetails
                              shipmentId={r.shipmentId}
                              delayProbability={delayProbNum}
                              sourceCountry={r.sourceCountry}
                              destination={r.destination}
                              supplierName={r.supplierName}
                            />
                          </Box>
                        )}
                      </CardContent>
                    </Card>
                  );
                })
              )}
            </Stack>
          ) : (
            <Card variant="outlined" sx={{ borderRadius: 2 }}>
              <CardContent sx={{ p: 0 }}>
                <TableContainer>
                  <Table size="small">
                    <TableHead>
                      <TableRow sx={{ bgcolor: '#f8fafc' }}>
                        <TableCell sx={{ fontWeight: 600, color: '#64748b', textTransform: 'uppercase', fontSize: '0.75rem' }}>S. NO.</TableCell>
                        <TableCell sx={{ fontWeight: 600, color: '#64748b', textTransform: 'uppercase', fontSize: '0.75rem' }}>PRODUCT</TableCell>
                        <TableCell sx={{ fontWeight: 600, color: '#64748b', textTransform: 'uppercase', fontSize: '0.75rem' }}>SUPPLIER</TableCell>
                        <TableCell sx={{ fontWeight: 600, color: '#64748b', textTransform: 'uppercase', fontSize: '0.75rem' }}>SOURCE COUNTRY</TableCell>
                        <TableCell sx={{ fontWeight: 600, color: '#64748b', textTransform: 'uppercase', fontSize: '0.75rem' }}>DESTINATION</TableCell>
                        <TableCell
                          onClick={handleSortClick}
                          sx={{ cursor: 'pointer', fontWeight: 600, color: '#64748b', textTransform: 'uppercase', fontSize: '0.75rem' }}
                        >
                          DELAY PROBABILITY {sortOrder === 'asc' ? '↑' : '↓'}
                        </TableCell>
                        <TableCell sx={{ fontWeight: 600, color: '#64748b', textTransform: 'uppercase', fontSize: '0.75rem' }}>CLASS</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {shipments.length === 0 ? (
                        <TableRow>
                          <TableCell colSpan={7} align="center" sx={{ py: 4 }}>
                            <Typography variant="body2" color="text.secondary">
                              No shipments found
                            </Typography>
                          </TableCell>
                        </TableRow>
                      ) : (
                        sortedShipments.map((r) => {
                          const delayProbNum = Number.parseInt(r.delayProbability.replace('%', ''), 10);
                          const probChipColor = getProbChipColor(delayProbNum);
                          const probChipBgColor = getProbChipBgColor(delayProbNum);
                          const isDelayed = r.predicted === "Delayed";
                          
                          return (
                            <React.Fragment key={r.shipmentId}>
                              <TableRow 
                                hover
                                onClick={() => setExpandedShipmentId(
                                  expandedShipmentId === r.shipmentId ? null : r.shipmentId
                                )}
                                sx={{ 
                                  cursor: 'pointer', 
                                  '&:hover': { bgcolor: '#f9fafb' },
                                  bgcolor: expandedShipmentId === r.shipmentId ? '#f0f9ff' : 'inherit'
                                }}
                              >
                                <TableCell>{r.sno}</TableCell>
                                <TableCell>{r.productName}</TableCell>
                                <TableCell>{r.supplierName}</TableCell>
                                <TableCell>{r.sourceCountry}</TableCell>
                                <TableCell>{r.destination}</TableCell>
                                <TableCell>
                                  <Chip 
                                    label={r.delayProbability} 
                                    size="small" 
                                    sx={{ 
                                      backgroundColor: probChipBgColor, 
                                      color: probChipColor,
                                      fontWeight: 600,
                                      border: "1px solid " + probChipColor + "20",
                                      '& .MuiChip-label': {
                                        px: 1.5
                                      }
                                    }} 
                                  />
                                </TableCell>
                                <TableCell>
                                  <Chip 
                                    label={r.predicted} 
                                    size="small" 
                                    sx={{ 
                                      backgroundColor: isDelayed ? '#fef2f2' : '#ecfdf5', 
                                      color: isDelayed ? '#ef4444' : '#10b981',
                                      fontWeight: 600,
                                      border: "1px solid " + (isDelayed ? '#fecaca' : '#a7f3d0'),
                                      '& .MuiChip-label': {
                                        px: 1.5
                                      }
                                    }} 
                                  />
                                </TableCell>
                              </TableRow>

                              {expandedShipmentId === r.shipmentId && (
                                <TableRow>
                                  <TableCell colSpan={7} sx={{ p: 2, bgcolor: '#f9fafb' }}>
                                    <ShipmentSHAPDetails 
                                      shipmentId={r.shipmentId} 
                                      delayProbability={delayProbNum}
                                      sourceCountry={r.sourceCountry}
                                      destination={r.destination}
                                      supplierName={r.supplierName}
                                    />
                                  </TableCell>
                                </TableRow>
                              )}
                            </React.Fragment>
                          );
                        })
                      )}
                    </TableBody>
                  </Table>
                </TableContainer>
                <TablePagination
                  rowsPerPageOptions={[10, 25, 50, 100]}
                  component="div"
                  count={totalRows}
                  rowsPerPage={rowsPerPage}
                  page={page}
                  onPageChange={handleChangePage}
                  onRowsPerPageChange={handleChangeRowsPerPage}
                  sx={{ borderTop: '1px solid #e5e7eb' }}
                />
              </CardContent>
            </Card>
          )}
        </>
      )}
    </Box>
  );
};

export default OverviewPanel;
